import React from 'react';
import { Brain, Camera, Dumbbell, Utensils, Refrigerator, MapPin, Footprints, LineChart, Repeat, Link2, ArrowRight } from 'lucide-react';

interface FeaturesSectionProps {
  observeEl: (el: HTMLElement | null) => void;
}

const BENTO_FEATURES = [
  {
    icon: Brain,
    title: 'Neural Planner',
    description: 'AI workout plan generator that creates personalized gym routines based on your goals, equipment, and fitness level. Your personal AI coach.',
    color: 'text-[#d277ff]',
    glow: 'bg-[#d277ff]/10',
    containerClass: 'bg-[#d277ff]/5',
    span: 'sm:col-span-2',
    tag: { text: 'AI Powered', color: 'text-[#d277ff] bg-[#d277ff]/10 border-[#d277ff]/20' },
    image: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=700&q=60&fm=webp',
    imageAlt: 'Fitness&Me Exercise Database',
    learnMore: { href: '/ai-planner-and-analyzer.html', label: 'See AI Planner details' },
  },
  {
    icon: Camera,
    title: 'Vision Scanner',
    description: 'AI calorie counter from photo. Snap a photo of your meal or restaurant menu for instant calorie estimates.',
    color: 'text-[#00f1fe]',
    glow: 'bg-[#00f1fe]/10',
    containerClass: 'bg-[#00f1fe]/5',
    span: '',
  },
  {
    icon: Dumbbell,
    title: 'Gym Tracker',
    description: 'Complete gym workout log app. Log every rep, set, and weight to track your strength training progress.',
    color: 'text-[#cafd00]',
    glow: 'bg-[#cafd00]/10',
    containerClass: 'bg-[#cafd00]/5',
    span: '',
    image: 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&w=700&q=60&fm=webp',
    imageAlt: 'Fitness&Me Gym Tracker',
    learnMore: { href: '/workout.html', label: 'See workout tracker' },
  },
  {
    icon: Utensils,
    title: 'Bio-Fueling',
    description: 'AI nutrition tracking with macro counting. Search 2.9M+ foods. Your complete gym and nutrition app.',
    color: 'text-[#ff7351]',
    glow: 'bg-[#ff7351]/10',
    containerClass: 'bg-[#ff7351]/5',
    span: '',
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=700&q=60&fm=webp',
    imageAlt: 'Fresh healthy salad with vegetables',
    learnMore: { href: '/nutrition.html', label: 'See nutrition tracker' },
  },
  {
    icon: Link2,
    title: 'Supersets & Combo Sets',
    description: 'Per-pair linking — not the whole workout. Link any two exercises into a Superset, or two sets of the same lift into a Combo Set. Visual stripes mark each pair, Smart Scroll snaps between grouped cards, and the rest timer aligns with the pair so you never rest mid-combo.',
    color: 'text-[#cafd00]',
    glow: 'bg-[#cafd00]/10',
    containerClass: 'bg-[#cafd00]/5',
    span: '',
    tag: { text: 'Set Linking', color: 'text-[#cafd00] bg-[#cafd00]/10 border-[#cafd00]/20' },
    learnMore: { href: '/workout.html', label: 'See how it works' },
  },
  {
    icon: Refrigerator,
    title: 'Fridge AI',
    description: 'AI fridge scanner identifies items, tracks freshness, suggests rescue recipes, and builds smart grocery lists.',
    color: 'text-[#00f1fe]',
    glow: 'bg-[#00f1fe]/10',
    containerClass: 'bg-[#00f1fe]/5',
    span: '',
  },
  {
    icon: MapPin,
    title: 'Gym Finder',
    description: 'Find nearby gyms anywhere in the world. Perfect for gym goers who travel and never want to miss a workout.',
    color: 'text-[#cafd00]',
    glow: 'bg-[#cafd00]/10',
    containerClass: 'bg-[#cafd00]/5',
    span: '',
  },
  {
    icon: LineChart,
    title: 'AI Analyzer',
    description: 'AI reports every two weeks that audit your training, nutrition and adherence — flag mistakes, surface progress, and tell you exactly what to fix next.',
    color: 'text-[#d277ff]',
    glow: 'bg-[#d277ff]/10',
    containerClass: 'bg-[#d277ff]/5',
    span: '',
    tag: { text: 'AI Insights', color: 'text-[#d277ff] bg-[#d277ff]/10 border-[#d277ff]/20' },
  },
  {
    icon: Repeat,
    title: 'DUP-3 + APRE-10',
    description: 'Two advanced protocols built in: DUP-3 rotates strength, hypertrophy and power across the week; APRE-10 (Mann) autoregulates your weights from each AMRAP set. No more guesswork.',
    color: 'text-[#ff7351]',
    glow: 'bg-[#ff7351]/10',
    containerClass: 'bg-[#ff7351]/5',
    span: '',
    tag: { text: 'Smart Programming', color: 'text-[#ff7351] bg-[#ff7351]/10 border-[#ff7351]/20' },
  },
  {
    icon: Footprints,
    title: 'Walking Tracker',
    description: 'Track walking workouts with GPS, steps, and calories. Interval walking programs for an active lifestyle.',
    color: 'text-[#d277ff]',
    glow: 'bg-[#d277ff]/10',
    containerClass: 'bg-[#d277ff]/5',
    span: 'sm:col-span-2',
    tag: { text: 'GPS Enabled', color: 'text-[#d277ff] bg-[#d277ff]/10 border-[#d277ff]/20' },
    image: 'https://images.unsplash.com/photo-1560741282-a3ff4a507b4f?auto=format&fit=crop&w=1200&q=70',
    imageAlt: 'Fitness&Me Walking Tracker',
  },
];

export function FeaturesSection({ observeEl }: FeaturesSectionProps) {
  return (
    <section className="py-20 px-6 sm:px-8 relative" id="features">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12" ref={el => observeEl(el)}>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#a8abb3] font-bold block mb-4">Core Intelligence</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-headline font-bold tracking-tighter text-[#f1f3fc]">
            Save time and train on your schedule.<br />
            <span className="text-[#a8abb3]">Smarter workouts that lead to progress.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENTO_FEATURES.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className={`rounded-[32px] bg-[#1b2028] relative overflow-hidden border border-[#44484f]/10 hover:border-[#44484f]/30 transition-all group ${feature.span} ${feature.image ? 'flex flex-col md:flex-row' : 'p-8'}`}
                ref={el => observeEl(el)}
                style={{ transitionDelay: `${index * 0.06}s` }}
              >
                {feature.image && (
                  <div className="relative w-full md:w-2/5 h-44 md:h-auto md:min-h-[280px] overflow-hidden flex-shrink-0">
                    <img
                      src={feature.image}
                      alt={feature.imageAlt || feature.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#1b2028] to-transparent md:hidden" />
                    <div className="hidden md:block absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#1b2028] to-transparent" />
                  </div>
                )}
                <div className={`relative ${feature.image ? 'p-8 flex-1' : ''}`}>
                  <div className={`absolute -top-4 -right-4 w-24 h-24 ${feature.glow} blur-3xl`} />
                  <div className="relative z-10">
                    <div className="mb-6 flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl ${feature.glow} border border-[#44484f]/10 flex items-center justify-center`}>
                        <Icon className={`h-6 w-6 ${feature.color}`} />
                      </div>
                      <h3 className="text-xl font-headline font-bold text-[#f1f3fc]">{feature.title}</h3>
                    </div>
                    <p className="text-[#a8abb3] leading-relaxed mb-4">{feature.description}</p>
                    {feature.tag && (
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${feature.tag.color}`}>
                        {feature.tag.text}
                      </span>
                    )}
                    {feature.learnMore && (
                      <a
                        href={feature.learnMore.href}
                        data-testid={`feature-learn-more-${feature.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                        className={`inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full border border-current/30 text-sm font-bold tracking-tight ${feature.color} hover:gap-3 hover:bg-current/10 transition-all`}
                      >
                        {feature.learnMore.label}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
